import React, { useEffect, useRef, useState } from "react";
import Switch from "@mui/material/Switch";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import DropdownList from "controls/DropdownList";
import { daysOfWeek, operationHours, parseCurrentDoorLockHours } from "../Utils";

interface LocationDoorLockHoursProps {
  showTitle?: boolean;
  editMode?: boolean;
  doorLockAlwaysLocked?: boolean;
  setDoorLockAlwaysLocked?: (value: boolean) => void;
  currentHours?: any;
  saveHours?: (hours: any) => void;
}

export default function LocationDoorLockHours({
  showTitle,
  editMode,
  doorLockAlwaysLocked,
  setDoorLockAlwaysLocked,
  currentHours,
  saveHours,
}: LocationDoorLockHoursProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [doorHours, setDoorHours] = useState(
    currentHours
      ? parseCurrentDoorLockHours(currentHours)
      : daysOfWeek.reduce(
          (acc: any, day: string) => ({
            ...acc,
            [day]: { lock: "12:00 AM", unlock: "8:00 AM", doNotLock: false, lockAllDay: true },
          }),
          {}
        )
  );

  const prevRef = useRef(doorHours);

  useEffect(() => {
    if (
      saveHours &&
      JSON.stringify(prevRef.current) !== JSON.stringify(doorHours)
    ) {
      saveHours(doorHours);
      prevRef.current = doorHours;
    }
  }, [doorHours, saveHours]);

  useEffect(() => {
    if (JSON.stringify(currentHours) !== JSON.stringify(doorHours)) {
      setDoorHours(parseCurrentDoorLockHours(currentHours));
    }
  }, [currentHours]);

  const handleChangeTime = (day: string, type: "lock" | "unlock", value: string) => {
    setDoorHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [type]: value === "Stay Locked" ? "11:59 PM" : value,
      },
    }));
  };

  const getFormattedData = (includeDoNotUnlock?: boolean) => {
    const data = [...operationHours];
    if (includeDoNotUnlock) data.unshift({ id: -1, name: "Stay Locked" });
    return data;
  };

  return (
    <MDBox width="100%" px={1} py={1}>
      {showTitle && (
        <MDTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
          {editMode ? "Select Door Lock Hours" : "Door Lock Hours"}
        </MDTypography>
      )}

      {/* --- ALWAYS LOCK SWITCH --- */}
      {editMode && (
        <MDBox>
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                borderBottom={!doorLockAlwaysLocked ? "1px solid #ddd" : "none"}
                >
                <MDTypography variant="body2" fontWeight="medium">
                    Always Require Door Code
                </MDTypography>
                <Switch
                    checked={doorLockAlwaysLocked}
                    onChange={(e) => setDoorLockAlwaysLocked?.(e.target.checked)}
                    color="info"
                />
            </MDBox>
            {doorLockAlwaysLocked && 
            <MDBox>
                <MDTypography variant="caption">
                    Location always requires door lock code - toggle off to set specific door lock hours.
                </MDTypography>
            </MDBox>
            }
        </MDBox>
        
      )}

      {/* --- FULL DOOR HOURS EDITING --- */}
      {editMode && !doorLockAlwaysLocked && (
        <MDBox>
          {daysOfWeek.map((day) => (
            <MDBox key={day} mb={2}>
              {/* Day Label */}
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="body2" fontWeight="bold">
                  {day}
                </MDTypography>
              </MDBox>

              {/* Switches: Not Locked / Locked All Day */}
              <MDBox display="flex" alignItems="center" gap={2} mt={0.5}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption">Not Locked</MDTypography>
                  <Switch
                    checked={doorHours[day].doNotLock}
                    onChange={(e) =>
                      setDoorHours((prev) => ({
                        ...prev,
                        [day]: {
                          lock: "12:00 AM",
                          unlock: "8:00 AM",
                          doNotLock: e.target.checked,
                          lockAllDay: e.target.checked === true ? false : doorHours[day].lockAllDay,                           
                        },
                      }))
                    }
                    color="info"
                  />
                </MDBox>

                <MDBox display="flex" alignItems="center" gap={1}>
                  <MDTypography variant="caption">Locked All Day</MDTypography>
                  <Switch
                    checked={doorHours[day].lockAllDay}
                    onChange={(e) =>
                      setDoorHours((prev) => ({
                        ...prev,
                        [day]: {
                          lock: "12:00 AM",
                          unlock: "8:00 AM",
                          lockAllDay: e.target.checked,
                          doNotLock: e.target.checked === true ? false : doorHours[day].doNotLock
                        },
                      }))
                    }
                    color="info"
                  />
                </MDBox>
              </MDBox>

              {/* Dropdowns for specific times */}
              {!doorHours[day].lockAllDay && !doorHours[day].doNotLock && (
                <MDBox display="flex" alignItems="center" gap={2} mt={1}>
                  <DropdownList
                    data={getFormattedData()}
                    value={doorHours[day].lock}
                    setValue={(v) => handleChangeTime(day, "lock", v)}
                    caption=""
                    label="Lock"
                  />
                  <MDTypography variant="body2">-</MDTypography>
                  <DropdownList
                    data={getFormattedData(true)}
                    value={doorHours[day].unlock === "11:59 PM" ? "Stay Locked" : doorHours[day].unlock}
                    setValue={(v) => handleChangeTime(day, "unlock", v)}
                    caption=""
                    label="Unlock"
                  />
                </MDBox>
              )}
            </MDBox>
          ))}
        </MDBox>
      )}

      {/* --- READ-ONLY MODE --- */}
      {!editMode && (
        <MDBox>
          {doorLockAlwaysLocked ? (
            <MDTypography textAlign="center" variant="body2">
              Always Require Door Code
            </MDTypography>
          ) : (
            daysOfWeek.map((day) => (
              <MDBox key={day} mb={1}>
                <MDTypography variant="body2" fontWeight="bold">
                  {day}
                </MDTypography>

                {doorHours[day].lockAllDay ? (
                  <MDTypography variant="caption">Locked All Day</MDTypography>
                ) : doorHours[day].doNotLock ? (
                  <MDTypography variant="caption">Not Locked</MDTypography>
                ) : (
                  <MDTypography variant="caption">
                    Locked at {doorHours[day].lock} –{" "}
                    {doorHours[day].unlock === "11:59 PM" ? "Stay Locked" : `Unlocked at ${doorHours[day].unlock}`}
                  </MDTypography>
                )}
              </MDBox>
            ))
          )}
        </MDBox>
      )}
    </MDBox>
  );
}
